import { Attachment, CompanyService, Image, Item, ItemConditionEnum, Move, MoveLine, Shipment, ShipmentItemGroupConfiguration, Tag, Warehouse, WarehouseLocation } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { DataFetcherFunction, PaginatedResults, requestFilterInterface } from '@/compiler/types';
import baseAxios, { fetchPaginatedDataFunction } from '@/helpers/forms/axios';
import { trackingNumberInterface } from '../trackingNumbers';
import { UpdateShipmentRequestData } from './outbound';

export interface InboundShipment extends Shipment {
    id: number
    createdAt: string
    updatedAt: string
    name: string
    merchantId: number
    notes: string | null
    internalNotes: string | null
    serviceLines: Array<InboundShipmentServiceLine<undefined, CompanyService>>
    attachments?: Attachment[]
    warehouseId: number
    warehouse?: Warehouse
    status: InboundShipmentStatus
    receivedAt: string
    referenceId: string
    trackingNumbers: trackingNumberInterface[]
    tags: Tag[]
    searchableTags: string[]
    shippedItemsCount: number
    actualQuantity: number
    expectedQuantity: number
    unsellableQuantity: number
}

export type InboundShipmentStatus = 'open' | 'shipped' | 'received';

export interface InboundShipmentServiceLine<
    SH extends InboundShipment | undefined = undefined,
    S extends CompanyService | undefined = undefined,
> {
    id: number
    createdAt: string
    updatedAt: string
    companyServiceId: number
    inboundShipmentId: number
    quantity: number

    shipment: SH
    service: S
}

export interface InboundShipmentItem {
    id: number
    item: Item
    itemId: number
    shipmentId: number
    expected: {
        quantity: number
        itemGroupConfigurations: ShipmentItemGroupConfiguration[]
    }
    actual: {
        quantity: number
        itemGroupConfigurations: ShipmentItemGroupConfiguration[]
        moves: Array<Move<MoveLine<undefined, WarehouseLocation>>>
    }
    unsellable: {
        quantity: number
    }
}

export type UpdateInboundShipmentItemRequestData = Pick<InboundShipmentItem, 'shipmentId' | 'itemId' | 'expected' | 'actual'>;

export interface InboundShipmentLiveTrackingInfo {
    status: 'pending' | 'transit' | 'delivered' | 'returned' | 'failure' | 'unknown'
    number: number | null
    eta: string | null
}

export interface UnsellableItem {
    id: number
    itemId: number
    images: Image[]
    notes: string
    receivedCondition?: ItemConditionEnum
    quantity: number
    item: Item
    shipmentId: number
    shipment: InboundShipment
}

export default (axios: AxiosInstance = baseAxios): { fetchInboundShipments: (page: number, filters?: requestFilterInterface[]) => Promise<PaginatedResults<InboundShipment>>; fetchInboundShipment: (shipmentId: number) => Promise<InboundShipment>; createInboundShipment: (name: string, warehouseId: number, notes?: string) => Promise<{ shipmentId: number; }>; deleteInboundShipment: (shipmentId: number) => Promise<void>, updateInboundShipment: (shipmentId: number, data: UpdateShipmentRequestData) => Promise<InboundShipment>; fetchInboundShipmentLiveTracking: (shipmentId: number) => Promise<InboundShipmentLiveTrackingInfo[]>; untagInboundShipment: (shipmentId: number, tagId: number) => Promise<void>; tagInboundShipment: (shipmentId: number, tagId: number) => Promise<void>; loadArchivedShipments: (page: number) => Promise<PaginatedResults<InboundShipment>>; batchArchive: (shipmentIds: number[]) => Promise<void>; batchUnarchive: (shipmentIds: number[]) => Promise<void>; fetchInboundShipmentItems: (shipmentId: number) => Promise<InboundShipmentItem[]>; fetchUnsellableItems: (preRequest?: (uri: URL) => URL) => DataFetcherFunction<UnsellableItem>; fetchUnsellableItemsForShipment: (shipmentId: number, preRequest?: (uri: URL) => URL) => DataFetcherFunction<UnsellableItem>; addInboundShipmentItem: (shipmentId: number, itemId: number, quantity: number) => Promise<void>; manageInboundShipmentServices: (shipmentId: number, services: Array<{ id: number; quantity: number; }>) => Promise<void>; updateInboundShipmentItem: (item: UpdateInboundShipmentItemRequestData) => Promise<void>; deleteInboundShipmentItem: (shipmentId: number, itemId: number) => Promise<void>; addTrackingNumbers: (shipmentId: number, carrier: string, trackingNumbers: string[]) => Promise<void>; createUnsellableItem: (shipmentId: number, data: CreateUnsellableItemRequestData) => Promise<void>; updateUnsellableItem: (unsellableItemId: number, data: UpdateUnsellableItemRequestData) => Promise<void>; deleteUnsellableItem: (unsellableItemId: number) => Promise<void>; deleteUnsellableItemImage: (image_id: number) => Promise<string>; receiveInboundShipment: (shipmentId: number) => Promise<void>; submitInboundShipment: (shipmentId: number) => Promise<void>; } => ({
    fetchInboundShipments: async (page: number, filters?: requestFilterInterface[]): Promise<PaginatedResults<InboundShipment>> => {
        const url = new URL('/api/shipments/inbound', location.origin);

        url.searchParams.append('page', page.toString());
        if (filters) {
            filters.forEach((filter) => {
                url.searchParams.append(`filters[${filter.name}]`, filter.value);
            });
        }

        const response = await axios.get<PaginatedResults<InboundShipment>>(url.toString());

        return response.data;
    },

    fetchInboundShipment: async (shipmentId: number): Promise<InboundShipment> => {
        const response = await axios.get<{ shipment: InboundShipment }>(`/api/shipments/inbound/${shipmentId}`);

        return response.data.shipment;
    },

    createInboundShipment: async (name: string, warehouseId: number, notes?: string): Promise<{ shipmentId: number }> => {
        const response = await axios.post<{ shipmentId: number }>('/api/shipments/inbound', {
            name,
            warehouseId,
            notes,
        });

        return response.data;
    },

    deleteInboundShipment: async (shipmentId: number): Promise<void> => {
        await axios.delete(`/api/shipments/inbound/${shipmentId}`);

        return;
    },

    updateInboundShipment: async (shipmentId: number, data: UpdateShipmentRequestData): Promise<InboundShipment> => {
        const response = await axios.put<{ shipment: InboundShipment }>(`/api/shipments/inbound/${shipmentId}`, data);

        return response.data.shipment;
    },

    fetchInboundShipmentLiveTracking: async (shipmentId: number): Promise<InboundShipmentLiveTrackingInfo[]> => {
        const response = await axios.get<{ tracking: InboundShipmentLiveTrackingInfo[] }>(`/api/shipments/inbound/${shipmentId}/track`);

        return response.data.tracking;
    },

    untagInboundShipment: async (shipmentId: number, tagId: number): Promise<void> => {
        return await axios.delete(`/api/shipments/inbound/${shipmentId}/tags/${tagId}`);
    },

    tagInboundShipment: async (shipmentId: number, tagId: number): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/tags/${tagId}`);
    },

    loadArchivedShipments: async (page: number): Promise<PaginatedResults<InboundShipment>> => {
        const response = await axios.get<PaginatedResults<InboundShipment>>(`/api/shipments/inbound/archived?page=${page}`);

        return response.data;
    },

    batchArchive: async (shipmentIds: number[]): Promise<void> => {
        return await axios.post('/api/shipments/inbound/batch/archive', {
            shipmentIds,
        });
    },

    batchUnarchive: async (shipmentIds: number[]): Promise<void> => {
        return await axios.post('/api/shipments/inbound/batch/unarchive', {
            shipmentIds,
        });
    },

    fetchInboundShipmentItems: async (shipmentId: number): Promise<InboundShipmentItem[]> => {
        const response = await axios.get<{ items: InboundShipmentItem[] }>(`/api/shipments/inbound/${shipmentId}/items`);

        return response.data.items;
    },

    fetchUnsellableItems: (preRequest?: (uri: URL) => URL): DataFetcherFunction<UnsellableItem> => fetchPaginatedDataFunction<UnsellableItem>('/api/shipments/inbound/inbound-shipment-unsellable-item', preRequest, axios),

    fetchUnsellableItemsForShipment: (shipmentId: number, preRequest?: (uri: URL) => URL): DataFetcherFunction<UnsellableItem> => {
        return fetchPaginatedDataFunction<UnsellableItem>(`/api/shipments/inbound/${shipmentId}/inbound-shipment-unsellable-item`, preRequest, axios);
    },

    addInboundShipmentItem: async (shipmentId: number, itemId: number, quantity: number): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/add-item`, { itemId, quantity });
    },

    manageInboundShipmentServices: async (shipmentId: number, services: Array<{ id: number; quantity: number }>): Promise<void> => {
        await axios.post(`/api/shipments/inbound/${shipmentId}/services`, { services });
    },

    updateInboundShipmentItem: async (item: UpdateInboundShipmentItemRequestData): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${item.shipmentId}/update-item`, item);
    },

    deleteInboundShipmentItem: async (shipmentId: number, itemId: number): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/remove-item`, { itemId });
    },

    addTrackingNumbers: async (shipmentId: number, carrier: string, trackingNumbers: string[]): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/add-tracking`, { carrier, trackingNumbers });
    },

    createUnsellableItem: async (shipmentId: number, data: CreateUnsellableItemRequestData): Promise<void> => {
        const form = new FormData();
        form.append('item_id', data.itemId.toString());
        form.append('quantity', data.quantity.toString());
        form.append('notes', data.notes);
        if (data.receivedCondition) {
            form.append('received_condition', data.receivedCondition);
        }

        if (data.photos.length) {
            for (const photo of data.photos) {
                form.append('photos[]', photo);
            }
        }

        return await axios.post(`/api/shipments/inbound/${shipmentId}/inbound-shipment-unsellable-item`, form);
    },

    updateUnsellableItem: async (unsellableItemId: number, data: UpdateUnsellableItemRequestData): Promise<void> => {
        const form = new FormData();
        form.append('quantity', data.quantity.toString());
        form.append('notes', data.notes);
        if (data.receivedCondition) {
            form.append('received_condition', data.receivedCondition);
        }

        if (data.photos.length) {
            for (const photo of data.photos) {
                form.append('photos[]', photo);
            }
        }

        await axios.post(`/api/shipments/inbound/inbound-shipment-unsellable-item/${unsellableItemId}`, form);
    },

    deleteUnsellableItem: async (unsellableItemId: number): Promise<void> => {
        return axios.delete(`/api/shipments/inbound/inbound-shipment-unsellable-item/${unsellableItemId}`);
    },

    deleteUnsellableItemImage: (image_id: number): Promise<string> => axios
        .delete<{ message: string }>(`/api/shipments/inbound/unsellable-item-images/${image_id}`)
        .then(response => response.data.message),

    receiveInboundShipment: async (shipmentId: number): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/receive`);
    },

    submitInboundShipment: async (shipmentId: number): Promise<void> => {
        return await axios.post(`/api/shipments/inbound/${shipmentId}/submit`);
    },
});

export interface CreateUnsellableItemRequestData {
    itemId: number
    quantity: number
    notes: string
    receivedCondition?: ItemConditionEnum
    photos: File[]
}

export interface UpdateUnsellableItemRequestData {
    quantity: number
    notes: string
    receivedCondition?: ItemConditionEnum
    photos: File[]
}
